import React, { useEffect, useRef, useState } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  connections: number[];
  energy: number;
  waveOffset: number;
}

const Emergence2Visualization: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState<'turbulence' | 'currents' | 'whirlpool'>('turbulence');
  const [particleCount, setParticleCount] = useState(50);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();
  const timeRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    const initParticles = () => {
      particlesRef.current = Array.from({ length: particleCount }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 3,
        vy: (Math.random() - 0.5) * 3,
        connections: [],
        energy: Math.random(),
        waveOffset: Math.random() * Math.PI * 2,
      }));
    };

    initParticles();

    const animate = () => {
      timeRef.current += 0.016;
      const t = timeRef.current;

      // Deep ocean background fade
      ctx.fillStyle = 'rgba(0, 8, 20, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const particles = particlesRef.current;
      const connectionDistance = phase === 'turbulence' ? 50 : phase === 'currents' ? 100 : 140;
      const attractionForce = phase === 'turbulence' ? 0 : phase === 'currents' ? 0.01 : 0.015;

      particles.forEach((p, i) => {
        p.connections = [];
        particles.forEach((other, j) => {
          if (i === j) return;
          const dx = other.x - p.x;
          const dy = other.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            p.connections.push(j);
            if (phase !== 'turbulence') {
              p.vx += (dx / dist) * attractionForce;
              p.vy += (dy / dist) * attractionForce;
            }
          }
        });

        // Wave-like motion overlay
        p.vx += Math.sin(t * 2 + p.waveOffset) * 0.03;
        p.vy += Math.cos(t * 1.5 + p.waveOffset) * 0.02;

        // Whirlpool spiral toward center
        if (phase === 'whirlpool') {
          const dx = cx - p.x;
          const dy = cy - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const angle = Math.atan2(dy, dx) + Math.PI / 2;
          p.vx += Math.cos(angle) * 0.04 + dx / dist * 0.008;
          p.vy += Math.sin(angle) * 0.04 + dy / dist * 0.008;
        }

        p.x += p.vx;
        p.y += p.vy;
        p.vx *= phase === 'whirlpool' ? 0.985 : 0.97;
        p.vy *= phase === 'whirlpool' ? 0.985 : 0.97;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        p.x = Math.max(0, Math.min(canvas.width, p.x));
        p.y = Math.max(0, Math.min(canvas.height, p.y));
      });

      // Draw wavy flowing connections
      particles.forEach((p) => {
        p.connections.forEach(j => {
          const other = particles[j];
          const dx = other.x - p.x;
          const dy = other.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const alpha = 1 - dist / connectionDistance;

          // Wavy control point
          const wave = Math.sin(t * 3 + p.waveOffset) * 15;
          const mx = (p.x + other.x) / 2 + (-dy / dist) * wave;
          const my = (p.y + other.y) / 2 + (dx / dist) * wave;

          const hue = phase === 'turbulence' ? 200 : phase === 'currents' ? 185 : 170;
          ctx.strokeStyle = `hsla(${hue}, 80%, 60%, ${alpha * 0.5})`;
          ctx.lineWidth = phase === 'whirlpool' ? 1.5 : 0.8;
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.quadraticCurveTo(mx, my, other.x, other.y);
          ctx.stroke();
        });

        // Water molecule particle
        const size = phase === 'whirlpool' ? 3.5 + p.connections.length * 0.4 : 2.5;
        const hue = 180 + Math.sin(t + p.waveOffset) * 20;
        ctx.fillStyle = `hsl(${hue}, 80%, 65%)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fill();

        if (phase === 'whirlpool' && p.connections.length > 5) {
          ctx.fillStyle = `rgba(100, 220, 255, 0.15)`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, size + 6, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
    return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
  }, [phase, particleCount]);

  return (
    <div className="min-h-screen bg-[#000814] text-cyan-400 font-mono p-8">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl mb-4 text-cyan-300">
            OCEAN CURRENTS: FLUID EMERGENCE
          </h1>
          <p className="mb-4 text-cyan-600">
            Water molecules flow from turbulence into organized currents, converging into a whirlpool of collective motion.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <button
              onClick={() => setPhase('turbulence')}
              className={`px-6 py-3 border-2 transition-all ${
                phase === 'turbulence'
                  ? 'border-cyan-400 bg-cyan-900/30 text-cyan-300'
                  : 'border-cyan-700 text-cyan-600 hover:border-cyan-500'
              }`}
            >
              TURBULENCE
              <div className="text-xs mt-1">Chaotic flow, no direction</div>
            </button>
            <button
              onClick={() => setPhase('currents')}
              className={`px-6 py-3 border-2 transition-all ${
                phase === 'currents'
                  ? 'border-teal-400 bg-teal-900/30 text-teal-300'
                  : 'border-cyan-700 text-cyan-600 hover:border-cyan-500'
              }`}
            >
              CURRENTS
              <div className="text-xs mt-1">Streams align, flow emerges</div>
            </button>
            <button
              onClick={() => setPhase('whirlpool')}
              className={`px-6 py-3 border-2 transition-all ${
                phase === 'whirlpool'
                  ? 'border-blue-400 bg-blue-900/30 text-blue-300'
                  : 'border-cyan-700 text-cyan-600 hover:border-cyan-500'
              }`}
            >
              WHIRLPOOL
              <div className="text-xs mt-1">Spiral convergence, unified force</div>
            </button>
          </div>
          <div className="mb-4">
            <label className="block mb-2">Molecule Count: {particleCount}</label>
            <input
              type="range" min="20" max="150" value={particleCount}
              onChange={(e) => setParticleCount(parseInt(e.target.value))}
              className="w-full"
            />
          </div>
        </div>
        <canvas ref={canvasRef} className="w-full h-[500px] border-2 border-cyan-800 bg-[#000814]" />
      </div>
    </div>
  );
};

export default Emergence2Visualization;
